import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './ShowDetails.css';
import './EnterIconMerit.css'; // Reuse styles for consistency

const ShowDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [judge, setJudge] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const backendUrl = `${API_BASE_URL}`;

  useEffect(() => {
    const playerId = localStorage.getItem('playerId');
    console.log('Checking localStorage on load:', { playerId });
    setIsLoggedIn(!!playerId);

    async function fetchShowAndEntries() {
      try {
        const showResponse = await fetch(`${backendUrl}/merit-confo/shows/${id}`, {
          headers: playerId ? { 'player-id': playerId } : {},
        });
        if (!showResponse.ok) {
          const err = await showResponse.json();
          throw new Error(`HTTP ${showResponse.status}: ${err.error || showResponse.statusText}`);
        }
        const showData = await showResponse.json();
        console.log('Show data:', { name: showData.name, entryFee: showData.entryFee, judged: showData.judged, placings: showData.placings });
        setShow(showData);

        const judgeResponse = await fetch(
          `${backendUrl}/merit-confo/judges?name=${encodeURIComponent(showData.featuredJudge)}`,
          {
            headers: playerId ? { 'player-id': playerId } : {},
          }
        );
        if (!judgeResponse.ok) {
          console.warn('Judge not found, using placeholder');
        } else {
          const judgeData = await judgeResponse.json();
          setJudge(judgeData);
        }

        const entriesResponse = await fetch(`${backendUrl}/merit-confo/shows/${id}/entries`, {
          headers: playerId ? { 'player-id': playerId } : {},
        });
        if (!entriesResponse.ok) {
          const err = await entriesResponse.json();
          throw new Error(`HTTP ${entriesResponse.status}: ${err.error || entriesResponse.statusText}`);
        }
        const entriesData = await entriesResponse.json();
        console.log('Fetched entries:', entriesData);
        setEntries(entriesData);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(`Failed to load show: ${err.message}`);
        setLoading(false);
      }
    }
    fetchShowAndEntries();
  }, [id]);

  const isShowOpen = () => {
    if (!show) return false;
    const today = new Date();
    const endDate = new Date(show.endDate);
    return endDate >= today;
  };

  const handleEnterShow = () => {
    if (!isLoggedIn) {
      setError('Please log in to enter the show.');
      navigate('/login');
      return;
    }
    navigate(`/merit-confo/show/${id}/enter`);
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US');

  const generateShowBlurb = () => {
    if (!show) return '';
    const { name, region, location, startDate, endDate, venueElements, featuredJudge, specialtyBreed } = show;
    const { theme, decor, colorScheme, ringStyle, smallDogSetup } = venueElements;
    const [city, stateOrCountry] = location.split(', ').length === 2 ? location.split(', ') : [location, region];
    const venuePhrase = `The venue features a ${venueElements.layout.toLowerCase()} layout`;
    return `
      Step into the spotlight at the prestigious <strong>${name}</strong> dog show. Hosted in the heart of ${city}, ${stateOrCountry} (${region}) from ${formatDate(startDate)} to ${formatDate(endDate)}! The theme of this event is ${theme.toLowerCase()}. The venue is ${decor.toLowerCase()} in a stunning ${colorScheme.toLowerCase()} color palette. ${venuePhrase}, with rings defined by ${ringStyle.toLowerCase()}. Small dogs are examined ${smallDogSetup.toLowerCase()}. This ${specialtyBreed} Specialty Show will be judged by the esteemed ${featuredJudge}.
    `;
  };

  const getRubricTableData = () => {
    if (!show || !show.rubric || !show.rubric.components) return [];
    const tableData = [];
    show.rubric.components.forEach((component) => {
      tableData.push({ criteria: component.name, points: `${component.points}` });
    });
    show.rubric.bonuses.forEach((bonus) => {
      if (bonus.points > 0) {
        tableData.push({ criteria: bonus.name, points: `Up to ${bonus.points}` });
      }
    });
    return tableData;
  };

  // Enrich placings with entry data
  const enrichedPlacings = (show?.placings || []).map(placing => {
    const entry = entries.find(e => e._id === placing.entryId);
    if (!entry || !entry.dogId || !entry.playerId) {
      console.warn('Entry not found or incomplete for placing:', {
        placing,
        entryId: placing.entryId,
        availableEntryIds: entries.map(e => e._id),
        entryFound: !!entry,
        hasDogId: !!entry?.dogId,
        hasPlayerId: !!entry?.playerId,
      });
      return {
        ...placing,
        dogName: 'Unknown Name',
        breed: 'Unknown Breed',
        class: 'Unknown Class',
        breeder: 'Unknown Breeder',
      };
    }
    return {
      ...placing,
      dogName: entry.dogId.registeredName || 'Unknown Name',
      breed: entry.dogId.breed || 'Unknown Breed',
      class: entry.class || 'Unknown Class',
      breeder: entry.playerId.username || 'Unknown Breeder',
    };
  });
  console.log('Enriched placings:', enrichedPlacings);

  // Group placings by category
  const groupedPlacings = {
    Breed: enrichedPlacings.filter(p => ['Winners Dog', 'Winners Bitch', 'Best of Breed', 'Best of Opposite Sex', 'Best of Winners'].includes(p.award)),
    Group: enrichedPlacings.filter(p => ['Group First', 'Group Second', 'Group Third', 'Group Fourth'].includes(p.award)),
    BestInShow: enrichedPlacings.filter(p => ['Best in Show', 'Reserve Best in Show'].includes(p.award)),
    CACIB: enrichedPlacings.filter(p => p.award.includes('CACIB')),
  };

  // Group Breed awards by breed
  const breedGroups = {};
  groupedPlacings.Breed.forEach(placing => {
    const breed = placing.breed || 'Unknown Breed';
    if (!breedGroups[breed]) {
      breedGroups[breed] = [];
    }
    breedGroups[breed].push(placing);
  });

  if (loading) return <div className="show-details">Loading show details...</div>;
  if (error) return <div className="show-details" style={{ color: 'red' }}>{error}</div>;
  if (!show) return <div className="show-details">Show not found</div>;

  const rubricTableData = getRubricTableData();

  return (
    <div className="show-details">
      <div className="show-header">
        <h1>{show.name}</h1>
        <div className="show-actions">
          {isLoggedIn && (
            <button
              onClick={handleEnterShow}
              disabled={!isShowOpen()}
              className={`enter-show-btn ${isShowOpen() ? '' : 'disabled'}`}
            >
              {isShowOpen() ? `Enter ${show.name} (${show.entryFee} DFG$)` : 'Show Closed'}
            </button>
          )}
          <Link to="/meritconfoshowlist">
            <button className="back-btn">Back to Shows</button>
          </Link>
        </div>
      </div>

      {!isShowOpen() && !show.judged && (
        <div className="show-status-banner">
          Pending Official Placings
        </div>
      )}

      <div className="show-blurb" dangerouslySetInnerHTML={{ __html: generateShowBlurb() }} />

      <div className="show-content">
        <div className="show-info">
          <h2>Show Information</h2>
          <p><strong>Location:</strong> {show.location}, {show.region}</p>
          <p><strong>Dates:</strong> {formatDate(show.startDate)} - {formatDate(show.endDate)}</p>
          <p><strong>Status:</strong> {isShowOpen() ? 'Open' : show.judged ? 'Judged' : 'Closed'}</p>
          <p><strong>Specialty Breed:</strong> {show.specialtyBreed}</p>
          <p><strong>Entry Fee:</strong> {show.entryFee} DFG$</p>
          <h3>Venue Details</h3>
          <ul>
            <li><strong>Decor:</strong> {show.venueElements.decor}</li>
            <li><strong>Theme:</strong> {show.venueElements.theme}</li>
            <li><strong>Weather:</strong> {show.venueElements.weather}</li>
            <li><strong>Layout:</strong> {show.venueElements.layout}</li>
            <li><strong>Ring Style:</strong> {show.venueElements.ringStyle}</li>
            <li><strong>Color Scheme:</strong> {show.venueElements.colorScheme}</li>
            <li><strong>Small Dog Setup:</strong> {show.venueElements.smallDogSetup}</li>
          </ul>
          <h3>Judging Rubric</h3>
          {rubricTableData.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '14px' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f5f5f5' }}>
                    Criteria
                  </th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f5f5f5' }}>
                    Points
                  </th>
                </tr>
              </thead>
              <tbody>
                {rubricTableData.map((item, index) => (
                  <tr key={index}>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.criteria}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No judging rubric defined.</p>
          )}
        </div>
        <div className="judge-info">
          <h2>Judge</h2>
          <img
            src={judge?.artUrl !== 'TBD' ? judge.artUrl : 'https://via.placeholder.com/150?text=Judge+Image'}
            alt={show.featuredJudge}
            className="judge-image"
          />
          <p><strong>Name:</strong> {show.featuredJudge}</p>
          {judge?.description && <p><strong>Bio:</strong> {judge.description}</p>}
        </div>
      </div>
      <div className="entries">
        <h2>Entries</h2>
        {entries.length === 0 ? (
          <p>No entries yet.</p>
        ) : (
          <div className="entries-grid">
            {entries.map((entry) => (
              <div key={entry._id} className="entry-card">
                <a href={entry.dogId?.artUrl || '#'} target="_blank" rel="noopener noreferrer">
                  <img
                    src={entry.dogId?.artUrl || 'https://via.placeholder.com/150?text=Entry+Image'}
                    alt={entry.dogId?.registeredName || 'Dog'}
                    className="entry-thumbnail"
                  />
                </a>
                <p><strong>Dog:</strong> <Link to={`/dog/${entry.dogId?._id}`} className="dog-link">{entry.dogId?.registeredName || 'Unknown'}</Link></p>
                <p><strong>Owner:</strong> {entry.playerId?.username || 'Unknown'}</p>
                <p><strong>Breed:</strong> {entry.dogId?.breed || 'Unknown'}</p>
                <p><strong>Class:</strong> {entry.class}</p>
                <p><strong>Entry:</strong> <a href={entry.dogId?.artUrl || '#'} target="_blank" rel="noopener noreferrer" className="art-link">View Entry</a></p>
              </div>
            ))}
          </div>
        )}
      </div>
      {show.judged && enrichedPlacings.length > 0 && (
        <div className="placings">
          <h2>Official Placings</h2>
          <div className="placings-container">
            {Object.entries(groupedPlacings).map(([category, awards]) => (
              awards.length > 0 && (
                <div key={category} className="placings-group">
                  <h3>{category}</h3>
                  {category === 'Breed' ? (
                    Object.entries(breedGroups).map(([breed, breedAwards]) => (
                      breedAwards.length > 0 && (
                        <div key={breed} className="breed-group">
                          <h4 className="breed-subheader">{breed}</h4>
                          <div className="placings-grid">
                            {breedAwards.map((placing, index) => (
                              <div key={index} className={`placing-card ${placing.ribbon ? placing.ribbon.toLowerCase().replace('/', '-') : ''}`}>
                                <p>
                                  <strong>{placing.award}</strong>: {placing.dogName} ({placing.breed})
                                </p>
                                <p><strong>Owner:</strong> {placing.breeder}</p>
                                <p><strong>Class:</strong> {placing.class}</p>
                                <p><strong>AP:</strong> {placing.ap}</p>
                                <p><strong>IP:</strong> {
                                  placing.ip || (
                                    ['CACIB Dog', 'CACIB Bitch', 'Reserve CACIB Dog', 'Reserve CACIB Bitch', 'Best in Show', 'Reserve Best in Show', 'Best of Breed', 'Best of Opposite Sex', 'Best of Winners'].includes(placing.award)
                                      ? placing.majorWin ? 6 : 4
                                      : placing.majorWin ? 3 : 1
                                  )
                                }</p>
                                {placing.ribbon && (
                                  <p><strong>Ribbon:</strong> <span className={`ribbon ${placing.ribbon.toLowerCase().replace('/', '-')}`}>{placing.ribbon}</span></p>
                                )}
                                {placing.majorWin && <p><strong>Major Win</strong></p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    ))
                  ) : (
                    <div className="placings-grid">
                      {awards.map((placing, index) => (
                        <div key={index} className={`placing-card ${placing.ribbon ? placing.ribbon.toLowerCase().replace('/', '-') : ''}`}>
                          <p>
                            <strong>{placing.award}</strong>: {placing.dogName} ({placing.breed})
                          </p>
                          <p><strong>Owner:</strong> {placing.breeder}</p>
                          <p><strong>Class:</strong> {placing.class}</p>
                          <p><strong>AP:</strong> {placing.ap}</p>
                          <p><strong>IP:</strong> {
                            placing.ip || (
                              ['CACIB Dog', 'CACIB Bitch', 'Reserve CACIB Dog', 'Reserve CACIB Bitch', 'Best in Show', 'Reserve Best in Show', 'Best of Breed', 'Best of Opposite Sex', 'Best of Winners'].includes(placing.award)
                                ? placing.majorWin ? 6 : 4
                                : placing.majorWin ? 3 : 1
                            )
                          }</p>
                          {placing.ribbon && (
                            <p><strong>Ribbon:</strong> <span className={`ribbon ${placing.ribbon.toLowerCase().replace('/', '-')}`}>{placing.ribbon}</span></p>
                          )}
                          {placing.majorWin && <p><strong>Major Win</strong></p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowDetails;